import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { visualStrategiesApi } from '../../api/visualStrategies';

// Async thunks
export const fetchStrategies = createAsyncThunk(
  'visualStrategies/fetchStrategies',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await visualStrategiesApi.listStrategies(filters);
      return { 
        ...response, 
        append: filters?.append || false,
        pageDirection: filters?.pageDirection,
        currentKey: filters?.currentKey
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchStrategy = createAsyncThunk(
  'visualStrategies/fetchStrategy',
  async (strategyId, { rejectWithValue }) => {
    try {
      const response = await visualStrategiesApi.getStrategy(strategyId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createStrategy = createAsyncThunk(
  'visualStrategies/createStrategy',
  async (strategyData, { rejectWithValue }) => {
    try {
      const response = await visualStrategiesApi.createStrategy(strategyData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateStrategy = createAsyncThunk(
  'visualStrategies/updateStrategy',
  async ({ strategyId, updates }, { rejectWithValue }) => {
    try {
      const response = await visualStrategiesApi.updateStrategy(strategyId, updates);
      return { strategyId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteStrategy = createAsyncThunk(
  'visualStrategies/deleteStrategy',
  async (strategyId, { rejectWithValue }) => {
    try {
      await visualStrategiesApi.deleteStrategy(strategyId);
      return strategyId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchPublicTemplates = createAsyncThunk(
  'visualStrategies/fetchPublicTemplates',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await visualStrategiesApi.getPublicTemplates(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const compileStrategy = createAsyncThunk(
  'visualStrategies/compileStrategy',
  async (strategyId, { rejectWithValue }) => {
    try {
      const response = await visualStrategiesApi.compileStrategy(strategyId);
      return { strategyId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  // Strategy list
  strategies: [],
  strategiesLoading: false,
  strategiesError: null,
  
  // Current strategy (for editing)
  currentStrategy: null,
  currentStrategyLoading: false,
  currentStrategyError: null,
  
  // Public templates
  templates: [],
  templatesLoading: false,
  templatesError: null,
  
  // Filters and pagination
  filters: {
    limit: 50,
    cursor: null,
    tags: [],
    complexity: '',
    timeframe: ''
  },
  
  // Cursor-based pagination
  nextCursor: null,
  hasMore: false,
  
  // UI state
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isCompiling: false,
  
  // Success messages
  successMessage: null
};

const visualStrategiesSlice = createSlice({
  name: 'visualStrategies',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentStrategy: (state) => {
      state.currentStrategy = null;
      state.currentStrategyError = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearErrors: (state) => {
      state.strategiesError = null;
      state.currentStrategyError = null;
      state.templatesError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch strategies
      .addCase(fetchStrategies.pending, (state) => {
        state.strategiesLoading = true;
        state.strategiesError = null;
      })
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.strategiesLoading = false;
        const data = action.payload.data || action.payload;
        state.strategies = data.strategies || data;
        // Handle cursor-based pagination
        state.nextCursor = data.cursor || null;
        state.hasMore = state.nextCursor !== null && state.nextCursor !== undefined;
      })
      .addCase(fetchStrategies.rejected, (state, action) => {
        state.strategiesLoading = false;
        state.strategiesError = action.payload;
      })
      
      // Fetch single strategy
      .addCase(fetchStrategy.pending, (state) => {
        state.currentStrategyLoading = true;
        state.currentStrategyError = null;
      })
      .addCase(fetchStrategy.fulfilled, (state, action) => {
        state.currentStrategyLoading = false;
        state.currentStrategy = action.payload.strategy || action.payload;
      })
      .addCase(fetchStrategy.rejected, (state, action) => {
        state.currentStrategyLoading = false;
        state.currentStrategyError = action.payload;
      })
      
      // Create strategy
      .addCase(createStrategy.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createStrategy.fulfilled, (state, action) => {
        state.isCreating = false;
        state.successMessage = 'Strategy created successfully!';
        // Add to strategies list if it exists
        if (state.strategies) {
          state.strategies.unshift(action.payload);
        }
      })
      .addCase(createStrategy.rejected, (state, action) => {
        state.isCreating = false;
        state.strategiesError = action.payload;
      })
      
      // Update strategy
      .addCase(updateStrategy.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateStrategy.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.successMessage = 'Strategy updated successfully!';
        const { visual, ...metadata } = action.payload;
        
        // Update in strategies list
        const index = state.strategies.findIndex(s => s.strategyId === action.payload.strategyId);
        if (index !== -1) {
          state.strategies[index] = { ...state.strategies[index], ...metadata };
        }
        
        // Update current strategy metadata but preserve local visual state.
        // The API response may return stale visual data due to eventual
        // consistency, which would cause the graph to revert to the pre-edit
        // state. Since the user just saved their local graph, the local
        // visual data is always the source of truth after a save.
        if (state.currentStrategy?.strategyId === action.payload.strategyId) {
          state.currentStrategy = { ...state.currentStrategy, ...metadata };
        }
      })
      .addCase(updateStrategy.rejected, (state, action) => {
        state.isUpdating = false;
        state.currentStrategyError = action.payload;
      })
      
      // Delete strategy
      .addCase(deleteStrategy.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteStrategy.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.successMessage = 'Strategy deleted successfully!';
        
        // Remove from strategies list
        state.strategies = state.strategies.filter(s => s.strategyId !== action.payload);
        
        // Clear current strategy if it matches
        if (state.currentStrategy?.strategyId === action.payload) {
          state.currentStrategy = null;
        }
      })
      .addCase(deleteStrategy.rejected, (state, action) => {
        state.isDeleting = false;
        state.strategiesError = action.payload;
      })
      
      // Fetch public templates
      .addCase(fetchPublicTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchPublicTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload.templates || action.payload;
      })
      .addCase(fetchPublicTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.payload;
      })
      
      // Compile strategy
      .addCase(compileStrategy.pending, (state) => {
        state.isCompiling = true;
      })
      .addCase(compileStrategy.fulfilled, (state, action) => {
        state.isCompiling = false;
        state.successMessage = 'Strategy compiled successfully!';
        const { visual, ...metadata } = action.payload;
        
        // Update strategy with compiled info
        const index = state.strategies.findIndex(s => s.strategyId === action.payload.strategyId);
        if (index !== -1) {
          state.strategies[index] = { ...state.strategies[index], ...metadata };
        }
        
        // Also update currentStrategy if it's the one being compiled
        if (state.currentStrategy?.strategyId === action.payload.strategyId) {
          state.currentStrategy = { ...state.currentStrategy, ...metadata };
        }
      })
      .addCase(compileStrategy.rejected, (state, action) => {
        state.isCompiling = false;
        state.strategiesError = action.payload;
      });
  }
});

export const { setFilters, clearCurrentStrategy, clearSuccessMessage, clearErrors } = visualStrategiesSlice.actions;

export default visualStrategiesSlice.reducer; 
